import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft, Home, LogOut } from "lucide-react";
import Button from "../../components/ui/Button";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Full-page loading screen using landing page theme.
 */
export function CartomancerLoading({ message }) {
  const { t } = useTranslation();
  const label = message || t("carto.loading");
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      data-testid="cartomancer-loading"
    >
      <div className="text-center relative z-10">
        <div className="rune-border p-6 rounded-3xl bg-card/60 backdrop-blur inline-block mb-6 gold-glow">
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_3563a477-4b96-448c-b228-bf27feeae015/artifacts/a3flx1m9_logo-mytharion%20no%20nome.png"
            alt="Mytharion"
            className="w-24 h-24 object-contain animate-pulse"
          />
        </div>
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 text-gold animate-spin" />
          <p className="font-heading text-sm tracking-widest text-gold uppercase">{label}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Access denied screen when user has no license.
 */
export function CartomancerAccessDenied({ reason }) {
  const { t } = useTranslation();
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      data-testid="cartomancer-access-denied"
    >
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="rune-border p-4 rounded-3xl bg-card/50 backdrop-blur inline-block mb-6">
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_3563a477-4b96-448c-b228-bf27feeae015/artifacts/a3flx1m9_logo-mytharion%20no%20nome.png"
              alt="Mytharion"
              className="w-20 h-20 object-contain opacity-90"
            />
          </div>
          <h1
            className="font-display text-4xl md:text-5xl mb-3"
            style={{
              background:
                "linear-gradient(180deg, #d4af37 0%, #b8860b 50%, #d4af37 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("carto.accessDeniedTitle")}
          </h1>
          <p className="font-serif-fantasy italic text-parchment/90 text-lg">
            {t("carto.accessDeniedQuote")}
          </p>
        </div>

        <div className="rune-border bg-card/80 backdrop-blur rounded-lg p-8">
          <p className="text-muted-foreground mb-6 text-center">
            {reason || t("carto.accessDeniedReason")}
          </p>

          <div className="space-y-3">
            <Link to="/store">
              <Button variant="primary" size="lg" className="w-full" data-testid="access-denied-store-btn">
                {t("carto.gotoStore")}
              </Button>
            </Link>
            <Link to="/redeem">
              <Button variant="arcane" size="lg" className="w-full" data-testid="access-denied-redeem-btn">
                {t("carto.haveRedeem")}
              </Button>
            </Link>
            <Link to="/">
              <Button variant="secondary" size="lg" className="w-full" data-testid="access-denied-home-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("carto.backToHome")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Shared header shown across Cartomancer screens.
 */
export function CartomancerHeader({ title, subtitle, plan, backTo, backLabel, rightSlot }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const label = backLabel || t("carto.backLabel");
  return (
    <header
      className="sticky top-0 z-40 border-b border-gold/20 bg-card/85 backdrop-blur-lg"
      data-testid="cartomancer-header"
    >
      <div className="w-full px-4 md:px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {backTo ? (
            <Link
              to={backTo}
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-gold/30 hover:border-gold hover:bg-gold/10 transition-all text-gold font-heading text-xs tracking-wider uppercase"
              data-testid="cartomancer-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </Link>
          ) : (
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-gold/30 hover:border-gold hover:bg-gold/10 transition-all text-gold font-heading text-xs tracking-wider uppercase"
              data-testid="cartomancer-home-btn"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">{t("carto.backMytharion")}</span>
            </Link>
          )}
          <div className="hidden md:block h-6 w-px bg-gold/20" />
          <div className="min-w-0">
            <h1 className="font-display text-lg md:text-xl text-gold truncate leading-tight">
              {title || "Cartomancer"}
            </h1>
            {subtitle && (
              <p className="font-serif-fantasy italic text-xs text-muted-foreground truncate">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {rightSlot}
          {plan && (
            <span
              className={`hidden sm:inline-flex items-center gap-1 px-3 py-1 rounded-full border font-heading text-[10px] tracking-widest uppercase ${
                plan === "premium"
                  ? "border-arcane text-arcane bg-arcane/10"
                  : "border-gold text-gold bg-gold/10"
              }`}
              data-testid="cartomancer-plan-badge"
            >
              {plan === "premium" ? t("carto.planPremium") : t("carto.planBasic")}
            </span>
          )}
          {user && (
            <div className="hidden md:flex items-center gap-2 pr-2">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-7 h-7 rounded-full border border-gold/40"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gold/20 flex items-center justify-center text-gold text-xs font-heading">
                  {user.name?.[0]?.toUpperCase()}
                </div>
              )}
              <span className="font-heading text-xs text-parchment">{user.name}</span>
            </div>
          )}
          <button
            onClick={logout}
            title={t("carto.logout")}
            className="p-2 rounded-md border border-border hover:border-ruby/50 hover:text-ruby transition-all"
            data-testid="cartomancer-logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
